export class FordLincolnSrpTracker {
  private noResultsContainer: HTMLElement | null = null;
  private noResultsForm: HTMLElement | null = null;
  private pageNameNoVehicle: string = "";
  private showNoResultsFormButton: HTMLElement | null = null;
  private isFirstPageView: boolean = true;
  private vehicleModel: string = "";

  private readonly boundNoResultsHandler: (event: CustomEvent | null) => void;
  private readonly resultsHeirarchyPrefix: string =
    "shopping tools:search inventory-vls";
  private readonly pageNameBase = () =>
    `${this.pageNameNoVehicle.split(":vls")[0]}:vls`;

  public constructor() {
    this.boundNoResultsHandler = this.noResultsHandler.bind(this);
    this.initialize();
  }

  private getCategoryExtension(): string {
    const nameplate = globalThis.digitaldata.vehicle?.nameplate?.trim();
    if (!nameplate) return "";
    const category = globalThis.nameplateCategoryMap[nameplate];
    return `${
      category ??
      globalThis.GetCategoryExtensionFallback(nameplate, this.vehicleModel)
    }`;
  }

  private getFullVehicleExtension(): string {
    const nameplate = this.getNameplate();
    if (nameplate) {
      return `:${this.getCategoryExtension()}${nameplate}`;
    }
    return "";
  }

  private getMakeFromFilters(model: string): string {
    const filter = document.querySelector(
      `[data-dotagging-product-name="SRP 2.0|Filters"][value="${model}"]`
    ) as HTMLElement;
    return filter?.dataset?.makeFilterInput?.toLowerCase() ?? "";
  }

  private getNameplate(): string {
    const nameplate = globalThis.digitaldata.vehicle?.nameplate?.trim();
    if (nameplate) {
      return `:${nameplate}`;
    }
    return "";
  }

  private initialize(): void {
    this.noResultsForm = document.querySelector(`form[name="noResultsForm"]`);
    this.noResultsContainer = document.getElementById("zeroResults");
    this.showNoResultsFormButton =
      this.noResultsContainer?.querySelector("button#contactUs");
    this.pageNameNoVehicle = globalThis.digitaldata.page.pageNameNoVehicle;

    if (this.isVisible(this.noResultsForm)) {
      this.noResultsHandler(null);
    } else if (
      this.showNoResultsFormButton &&
      this.isVisible(this.noResultsContainer)
    ) {
      this.setNoResultsLandingPageName();
    }

    if (this.showNoResultsFormButton) {
      this.showNoResultsFormButton.addEventListener("click", () => {
        if (!this.isVisible(this.noResultsForm)) {
          return;
        }
        const pseudoEvent = {
          target: this.noResultsForm,
        } as unknown as CustomEvent;
        this.isFirstPageView
          ? globalThis.setDataLayerWithGlobalOverlay_NoResultsLoad(pseudoEvent)
          : this.setNoResultsGlobalOverlay();
      });
    }
    globalThis.addEventListener(
      "searchFilterUpdate",
      this.searchFilterUpdateHandler.bind(this)
    );
  }

  private isVisible(elem: HTMLElement | null): boolean {
    if (!elem) {
      return false;
    }
    if (elem.hidden) {
      return false;
    }
    const style = globalThis.getComputedStyle(elem);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    ) {
      return false;
    }
    const rect = elem.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  private noResultsHandler(event: CustomEvent | null): void {
    if (this.isVisible(this.noResultsForm)) {
      const pseudoEvent = {
        target: this.noResultsForm,
      } as unknown as CustomEvent;
      this.isFirstPageView
        ? globalThis.setDataLayerWithGlobalOverlay_NoResultsLoad(pseudoEvent)
        : this.setNoResultsGlobalOverlay();
    } else if (this.isVisible(this.showNoResultsFormButton)) {
      this.setNoResultsLandingPageName();
    }
    document.removeEventListener(
      "do.vehicleloadend",
      this.boundNoResultsHandler
    );
  }

  private searchFilterUpdateHandler(event: CustomEvent): void {
    this.isFirstPageView = false;
    if (event.detail?.count === 0) {
      document.addEventListener(
        "do.vehicleloadend",
        this.boundNoResultsHandler
      );
    }
    // Reset pageNameNoVehicle if filters change
    globalThis.digitaldata.page.pageNameNoVehicle = this.pageNameNoVehicle;
    const params = new URLSearchParams(event.detail.filters);
    const queryParams = new URLSearchParams();

    for (const [name, value] of Array.from(params.entries())) {
      queryParams.append(name.toLowerCase(), value);
    }

    let model = "";
    const modelAndTrim = queryParams.get("modelandtrim");
    if (modelAndTrim) {
      const models = modelAndTrim.split("!") ?? [];
      if (models.length === 1) {
        this.vehicleModel = model = models[0].split(":")[0];
      }
    } else {
      this.vehicleModel = "";
    }

    if (!globalThis.digitaldata.vehicle) {
      globalThis.digitaldata.vehicle = {};
    }
    if (model) {
      const modelLower = model.toLowerCase();
      const make_model =
        [`ford ${modelLower}`, `lincoln ${modelLower}`].find((prefix) =>
          Object.prototype.hasOwnProperty.call(
            globalThis.nameplateCategoryMap,
            prefix
          )
        ) ?? `${this.getMakeFromFilters(model)} ${modelLower}`;
      globalThis.digitaldata.vehicle.nameplate = make_model;
      globalThis.digitaldata.page.pageName = `${globalThis.digitaldata.page.pageNameNoVehicle}:${make_model}`;
      globalThis.digitaldata.page.hierarchy = `${
        this.resultsHeirarchyPrefix
      }${this.getFullVehicleExtension()}`;
    } else {
      this.vehicleModel = "";
      globalThis.digitaldata.vehicle.nameplate = "";
      globalThis.digitaldata.page.pageName =
        globalThis.digitaldata.page.pageNameNoVehicle;
      globalThis.digitaldata.page.hierarchy = this.resultsHeirarchyPrefix;
    }

    let modelYear = "";
    const yearrange_query_param = queryParams.get("yearrange");
    if (yearrange_query_param) {
      modelYear = yearrange_query_param.replace("-", " - ");
    }
    globalThis.digitaldata.vehicle.modelYear = modelYear;
  }

  private setNoResultsGlobalOverlay(): void {
    const pageNameBase = this.pageNameBase();
    globalThis.digitaldata.page.pageName = `${pageNameBase}:no results:contact${this.getNameplate()}`;
    globalThis.digitaldata.page.hierarchy = `shopping tools:contact${this.getFullVehicleExtension()}`;
    globalThis.digitaldata.page.pageNameNoVehicle = `${pageNameBase}:no results:contact`;
    if (typeof globalThis._satellite?.track === "function") {
      globalThis._satellite.track("global-overlay");
    } else {
      console.warn("'_satellite' is not defined");
    }
  }

  private setNoResultsLandingPageName(): void {
    const pageNameNoVehicle = this.pageNameBase();
    globalThis.digitaldata.page.hierarchy = "shopping tools:contact";
    globalThis.digitaldata.page.pageName = `${pageNameNoVehicle}:no results${this.getNameplate()}`;
    globalThis.digitaldata.page.pageNameNoVehicle = pageNameNoVehicle;
  }
}

document.addEventListener("do.vehicleloadend", initializeFordLincolnSrp);
setTimeout(() => {
  initializeFordLincolnSrp();
}, 3000);

function initializeFordLincolnSrp() {
  document.removeEventListener("do.vehicleloadend", initializeFordLincolnSrp);
  new FordLincolnSrpTracker();
}
